import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { setInFlowCheckoutStep } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import HeaderContactOptionsMenu from 'components/App/Header/HeaderContactOptions/HeaderContactOptionsMenu'
import NavigationHeaderPhoneMenu from 'components/App/Header/HeaderMain/NavigationHeader/NavigationHeaderPhoneMenu'
import TextButton from 'components/Luxkit/Button/TextButton'
import IconButton from 'components/Luxkit/Button/IconButton'
import CheckoutSaveButtonGuard from 'tripPlanner/components/EntryPoints/CheckoutSaveButtonGuard'
import CheckoutSaveButton from 'tripPlanner/components/EntryPoints/CheckoutSaveButton'
import { RouterLocation } from 'connected-react-router'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import { getCruiseCurrentStep, CRUISE_CHECKOUT_STEPS_V2 } from './steps/cruises'
import { getTourCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import CheckoutStepperProgress from './CheckoutStepperProgress'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CheckoutDevModeToggle from 'checkout/Components/Shared/CheckoutDevModeToggle'

const HeaderContainer = styled.div`
  display: grid;
  grid-template: "start-actions middle end-actions" auto / auto minmax(0, 1fr) auto;
  align-items: center;
  padding-block: ${rem(4)};
`

const StartActions = styled(Group)`
  grid-area: start-actions;
`

const EndActions = styled(Group)`
  grid-area: end-actions;
`

const Middle = styled(Group)`
  grid-area: middle;
`

interface Props extends MappedProps {
  onBack: () => void;
}

function StepsHeaderCheckout(props: Props) {
  const dispatch = useAppDispatch()
  const {
    onBack,
    inFlowCheckoutStep,
    location,
    isCruise,
    tourCheckoutSteps,
  } = props

  const currentCheckoutStep = useMemo(() => {
    if (isCruise) {
      return getCruiseCurrentStep(inFlowCheckoutStep, location.pathname)
    }

    if (tourCheckoutSteps.length > 1) {
      return getCheckoutRouteFromURL(location.pathname)
    }

    return ''
  }, [tourCheckoutSteps, isCruise, location.pathname, inFlowCheckoutStep])

  const steps = useMemo<Array<App.Checkout.CheckoutStep>>(() => {
    if (isCruise) {
      return CRUISE_CHECKOUT_STEPS_V2
    }

    if (tourCheckoutSteps.length > 1) {
      return tourCheckoutSteps
    }

    return []
  }, [tourCheckoutSteps, isCruise])

  const handlerBack = useCallback(() => {
    const flatSteps = steps.flatMap((step) => step.subSteps?.length ? step.subSteps : step)
    const stepIndex = flatSteps.findIndex((step) => step.key === currentCheckoutStep)
    const currentStep = flatSteps[stepIndex]
    const prevStep = flatSteps[stepIndex - 1]

    if (prevStep && !prevStep.isRoute) {
      dispatch(setInFlowCheckoutStep(prevStep.key))
    }

    if (!prevStep || prevStep.isRoute || currentStep.isRoute) {
      onBack()
    }
  }, [onBack, steps, dispatch, currentCheckoutStep])

  return <HeaderContainer>
    <StartActions direction="horizontal" gap={4} verticalAlign="center">
      <CSSBreakpoint min="largeDesktop">
        <TextButton
          outdent="left"
          kind="tertiary"
          size="large"
          startIcon={<LineArrowLeftIcon />}
          onClick={handlerBack}
        >
          Back
        </TextButton>
      </CSSBreakpoint>
      <CSSBreakpoint max="desktop">
        <IconButton
          outdent="left"
          kind="tertiary"
          size="large"
          onClick={handlerBack}
        >
          <LineArrowLeftIcon />
        </IconButton>
      </CSSBreakpoint>
      <CheckoutDevModeToggle />
    </StartActions>
    <Middle direction="horizontal" horizontalAlign="center">
      <CheckoutStepperProgress />
    </Middle>
    <EndActions direction="horizontal" gap={4}>
      <CSSBreakpoint min="largeDesktop">
        <NavigationHeaderPhoneMenu />
      </CSSBreakpoint>
      <CSSBreakpoint max="desktop">
        <HeaderContactOptionsMenu size="large" />
      </CSSBreakpoint>
      <CheckoutSaveButtonGuard>
        <CheckoutSaveButton />
      </CheckoutSaveButtonGuard>
    </EndActions>
  </HeaderContainer>
}

interface MappedProps {
  inFlowCheckoutStep: string | undefined;
  location: RouterLocation<unknown>;
  isCruise: boolean;
  tourCheckoutSteps: Array<App.Checkout.CheckoutStep>;
}

const mapStateToProps = (state: App.State): MappedProps => {
  return {
    inFlowCheckoutStep: state.cruise.inFlowCheckoutStep,
    location: state.router.location,
    isCruise: getCruiseItems(state).length > 0,
    tourCheckoutSteps: getTourCheckoutSteps(state),
  }
}

export default connect(mapStateToProps)(StepsHeaderCheckout)
